import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, CircularProgress, Paper } from '@mui/material';
import { toast } from 'react-toastify';
import { getFinancialReport } from './api/underwritingApi';
import ReportViewer from './MarkdownReport.js';
import DnbFinancialReportDashboard from './DnbFinancialReportDashboard';
import {
  createDnbFinancialReportPdf,
  createGenericFinancialReportPdf,
  parseDnbFinancialReport,
} from './utils/dnbFinancialReport';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

function FinancialReport({ tenantId, effectiveCompanyId, refreshKey = 0 }) {
  const [financialReport, setFinancialReport] = useState("");
  const [reportSource, setReportSource] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchFinancialReport = async () => {
      if (effectiveCompanyId) {
        setIsLoading(true);
        try {
          const result = await getFinancialReport(tenantId, effectiveCompanyId);
          if (result?.data) {
            setFinancialReport(result.data?.report || "");
            setReportSource(result.data?.source || null);
          }
        } catch (error) {
          console.error("Error fetching financial report:", error);
          setFinancialReport(null); // Set to null on error to distinguish from empty
          setReportSource(null);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    fetchFinancialReport();
  }, [tenantId, effectiveCompanyId, refreshKey]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      let logoBytes = null;
      try {
        const response = await fetch('/logo.png');
        if (response.ok) logoBytes = await response.arrayBuffer();
      } catch (error) {
        console.warn('Could not load the CrediArc logo for the PDF', error);
      }

      const isDnb = reportSource?.provider === 'D&B';
      const parsed = isDnb ? parseDnbFinancialReport(financialReport) : null;
      const bytes = isDnb
        ? await createDnbFinancialReportPdf({ report: parsed, logoBytes })
        : await createGenericFinancialReportPdf({ markdownText: financialReport, logoBytes });
      const companyName = parsed?.companyName || 'Company';
      const safeName = companyName.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'Company';
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `CrediArc-${safeName}-Financial-Report.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Branded financial report downloaded.');
    } catch (error) {
      console.error('Error exporting financial report', error);
      toast.error('Failed to download the financial report.');
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, height: '200px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Financial Report...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', color: '#1A2A56' }}>
          Financial Analysis
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {financialReport && (
            <Button
              variant="outlined"
              onClick={handleExport}
              disabled={isExporting}
              startIcon={isExporting ? <CircularProgress size={16} /> : <FileDownloadIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 'bold',
                borderRadius: '8px',
              }}
            >
              {isExporting ? 'Preparing PDF…' : 'Download PDF'}
            </Button>
          )}
        </Box>
      </Box>

      {financialReport ? (
        reportSource?.provider === 'D&B' ? (
          <DnbFinancialReportDashboard markdownText={financialReport} source={reportSource} />
        ) : (
          <Paper elevation={2} sx={{ p: 3, borderRadius: '8px', backgroundColor: '#fff' }}>
            <ReportViewer markdownText={financialReport} tenantId={tenantId} />
          </Paper>
        )
      ) : (
        <Paper elevation={0} sx={{ textAlign: 'center', p: 4, border: '1px dashed #ccc', borderRadius: '8px', backgroundColor: '#fafafa' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>No Financial Report Available</Typography>
          <Typography variant="body2" color="text.secondary">
            Click "Generate New Report" to create a new financial analysis.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

export default FinancialReport;
